'use client';
import React from 'react';
import { DateTime } from 'luxon';

import styled, { useTheme } from '@benzinga/themetron';
import { ErrorBoundary, Icon } from '@benzinga/core-ui';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp';

import Hooks from '@benzinga/hooks';
import { SessionContext } from '@benzinga/session-context';
import { Session } from '@benzinga/session';
import { Mover, Movers, MoversQuery, getAllMovers } from '@benzinga/movers-manager';

import StockMoversTable, { StockMoversLayoutType } from '../StockMoversTable';

export type StockMoversDisplay = 'all' | 'losers' | 'gainers';

export interface StockMoversProps {
  layout?: StockMoversLayoutType;
  query: MoversQuery;
  title?: string;
  display?: StockMoversDisplay;
  initialMovers?: Movers | null;
  adPlacement?: React.ReactElement;
  moversTableLayout?: string;
  hideTickerPopup?: boolean;
  gainersLabel?: string;
  losersLabel?: string;
}

interface TabBarProps {
  currentTab: MoverTab;
  handleChangeTab: (newTab: MoverTab) => void;
  tabs: MoverTab[];
}

interface TabProps {
  tab: MoverTab;
  onClick?: (tab: MoverTab) => void;
  currentTab?: MoverTab;
  variant?: 'button' | 'header';
}

interface MoverTab {
  label: string;
  key: string;
  color?: string;
  icon?: IconDefinition;
}

export const StockMovers: React.FC<StockMoversProps> = ({
  adPlacement,
  display = 'all',
  gainersLabel = 'Gainers',
  hideTickerPopup = false,
  initialMovers = null,
  layout = 'default',
  losersLabel = 'Loser',
  moversTableLayout = '',
  query = {},
  title = 'Stock Movers',
}) => {
  const [movers, setMovers] = React.useState<Movers | Record<string, never>>(initialMovers ?? {});
  const [isLoaded, setIsLoaded] = React.useState<boolean>(!!initialMovers);

  const theme = useTheme();
  const session = React.useContext(SessionContext);

  const tabs: MoverTab[] = [
    { color: theme.colorPalette.blue600, key: 'Top', label: 'Top' },
    { color: theme.colorPalette.green500, icon: faCaretUp, key: 'Gainers', label: gainersLabel },
    { color: theme.colorPalette.red500, icon: faCaretDown, key: 'Losers', label: losersLabel },
    // { label: 'Active', color: '#2ca2d1', data: movers?.active },
  ];

  let defaultTab = tabs[0];
  switch (display) {
    case 'gainers':
      defaultTab = tabs[1];
      break;
    case 'losers':
      defaultTab = tabs[2];
      break;
  }

  const getCurrentMovers = React.useCallback((key: string, movers: Movers | Record<string, never>): Mover[] => {
    let data: Mover[] = [];
    if (key === 'Top' && movers) {
      data = movers?.all;
    } else if (key === 'Gainers' && movers?.gainers) {
      data = movers?.gainers;
    } else if (key === 'Losers' && movers?.losers) {
      data = movers?.losers;
    }
    return data;
  }, []);

  const [currentTab, setCurrentTab] = React.useState<MoverTab>(defaultTab);
  const [currentData, setCurrentData] = React.useState<Mover[]>(getCurrentMovers(defaultTab.key, movers));

  const handleChangeTab = React.useCallback((newTab: MoverTab) => {
    setCurrentTab(newTab);
  }, []);

  React.useEffect(() => {
    (async () => {
      if (!isLoaded) {
        const _movers = await getAllMovers(session as unknown as Session, query);
        if (_movers) {
          setMovers(_movers);
        }
        setIsLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, query]);

  React.useEffect(() => {
    const data = getCurrentMovers(currentTab.key, movers);
    setCurrentData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab.label, movers]);

  const formatToSessionDate = (date: Date) => {
    const sessionDate = date
      ? DateTime.fromJSDate(date).setZone('America/New_York').toFormat('MMM d, yyyy h:mma ZZZZ')
      : null;

    return sessionDate;
  };

  const sessionFromDate = movers.fromDate ? formatToSessionDate(new Date(movers.fromDate)) : null;
  const sessionToDate = movers.toDate ? formatToSessionDate(new Date(movers.toDate)) : null;

  const ref = React.useRef<HTMLDivElement | null>(null);
  const { width } = Hooks.useRefElementSize(ref);

  const showCompactTable = width !== 0 ? width < 700 : layout !== 'default';

  const hideColumns = {
    averageVolume: width !== 0 ? width < 800 : layout !== 'default',
    companyName: layout === 'nano',
  };

  return (
    <Container className={`stock-movers-container ${layout}`} ref={ref}>
      <div className="header-container">
        <StyledLine className="styled-line" />
        <h2>{title}</h2>
        <StyledLine className="styled-line" />
      </div>
      {showCompactTable && (
        <div className="stock-movers-table-container compact-table">
          <TabBar currentTab={currentTab} handleChangeTab={handleChangeTab} tabs={tabs} />
          <ErrorBoundary name="stock-movers-table-compact-table">
            <StockMoversTable
              adPlacement={adPlacement}
              hideTickerPopup={hideTickerPopup}
              isLoading={!isLoaded}
              layout={layout}
              movers={currentData}
              options={{ hideColumns }}
              session={query?.session}
            />
          </ErrorBoundary>
        </div>
      )}
      {!showCompactTable && (
        <div
          className={`stock-movers-table-container full-width-table ${moversTableLayout} flex`}
          id="full-width-movers-table"
        >
          {(display === 'all' || display === 'gainers' || !display) && (
            <div>
              <Tab currentTab={tabs[1]} tab={tabs[1]} variant="header" />
              <ErrorBoundary name="stock-movers-table_full-width-table_gainers">
                <StockMoversTable
                  adPlacement={adPlacement}
                  hideTickerPopup={hideTickerPopup}
                  isLoading={!isLoaded}
                  layout="compact"
                  movers={movers ? movers.gainers : []}
                  options={{ hideColumns }}
                  session={query?.session}
                />
              </ErrorBoundary>
            </div>
          )}
          {(display === 'all' || display === 'losers' || !display) && (
            <div>
              <Tab currentTab={tabs[2]} tab={tabs[2]} variant="header" />
              <ErrorBoundary name="stock-movers-table_full-width-table_losers">
                <StockMoversTable
                  adPlacement={adPlacement}
                  hideTickerPopup={hideTickerPopup}
                  isLoading={!isLoaded}
                  layout="compact"
                  movers={movers ? movers.losers : []}
                  options={{ hideColumns }}
                  session={query?.session}
                />
              </ErrorBoundary>
            </div>
          )}
        </div>
      )}
      {movers.fromDate && movers.toDate && (
        <div className="session-time-container">
          <div className="stock-movers-timeframe">
            Session: {sessionFromDate} - {sessionToDate}
          </div>
        </div>
      )}
    </Container>
  );
};

const TabBar: React.FC<TabBarProps> = ({ currentTab, handleChangeTab, tabs }) => (
  <StyledTabBar className="stock-movers-tabbar">
    {tabs.map(tab => (
      <Tab currentTab={currentTab} key={tab.label} onClick={() => handleChangeTab(tab)} tab={tab} />
    ))}
  </StyledTabBar>
);

const Tab: React.FC<TabProps> = ({ currentTab, onClick, tab, variant = 'button' }) => (
  <StyledTab
    $backgroundColor={currentTab && currentTab.label === tab.label ? tab.color : '#d6d6d6'}
    $cursor={currentTab && currentTab.label === tab.label ? 'default' : 'pointer'}
    as={variant === 'header' ? 'header' : 'button'}
    className="tab"
    key={tab.label}
    onClick={variant === 'header' ? undefined : () => onClick && onClick(tab)}
  >
    {tab.icon && <Icon icon={tab.icon} style={{ marginRight: '2px' }} />}
    {variant === 'header' ? <h3>{tab.label}</h3> : <span>{tab.key}</span>}
  </StyledTab>
);

const Container = styled.div`
  &.stock-movers-container {
    width: 100%;

    .header-container {
      display: flex;
      align-items: center;
      margin-bottom: 0.5rem;

      > h2 {
        font-weight: 600;
        font-size: ${({ theme }) => theme.fontSize.sm};
        line-height: 1;
        padding-right: 0.5rem;
        white-space: nowrap;
      }

      .styled-line {
        &:nth-of-type(1) {
          display: none;
        }
        &:before {
          background-color: ${({ theme }) => theme.colors.border};
        }
      }

      @media screen and (min-width: 700px) {
        > h2 {
          padding: 0 0.5rem;
        }
        .styled-line {
          &:nth-of-type(1) {
            display: block;
          }
        }
      }
    }

    .session-time-container {
      height: 18px;
      margin-top: 0.5rem;
    }

    .stock-movers-timeframe {
      font-size: 12px;
      font-style: italic;
      color: #555;
    }

    .stock-movers-table-container {
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};

      th.volume-table-header,
      td.volume {
        display: none;
      }

      &.compact-table {
        .stock-movers-table-wrapper table {
          //min-width: 500px;
        }
      }

      &.full-width-table {
        > div {
          &:first-of-type {
            margin-right: 10px;
            border-right: 1px solid ${({ theme }) => theme.colors.border};
            padding-right: 10px;
          }
        }

        .tab {
          cursor: auto;
        }
      }
    }

    &.default {
      .stock-movers-table-container {
        &.full-width-table {
          th.volume-table-header,
          th.buy-stock-table-header,
          td.volume,
          td.buy {
            display: table-cell;
          }
          &.stacked {
            flex-direction: column;
            &.full-width-table {
              > div {
                &:first-of-type {
                  margin-right: unset;
                  border-right: unset;
                  padding-right: unset;
                }
              }
            }
            tbody {
              max-height: 100%;
              .ticker-box-wrapper {
                border: none;
              }
            }
          }
        }
      }
    }

    &.nano {
      th.average-volume,
      th.market-cap,
      td.average-volume,
      td.market-cap {
        display: none;
      }
    }

    &.compact {
      .stock-movers-table-container {
        &.full-width-table {
          display: none !important;
        }
        &.compact-table {
          display: block !important;
        }
      }
    }
  }
`;

const StyledLine = styled.div`
  flex-grow: 1;
  position: relative;
  width: 100%;
  height: 1px;

  &:before {
    content: '';
    width: 100%;
    height: 1px;
    display: block;
    background-color: ${({ theme }) => theme.colors.foregroundMuted};
    position: absolute;
    top: 50%;
  }
`;

const StyledTabBar = styled.div`
  &.stock-movers-tabbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding-bottom: 10px;
  }
`;

const StyledTab = styled.button<{ $backgroundColor?: string; $cursor?: string }>`
  &.tab {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    font-weight: 700;
    font-size: 12px;
    line-height: 1.25rem;
    padding-top: 0.1rem;
    padding-bottom: 0.1rem;
    padding-left: 0.125rem;
    padding-right: 0.125rem;
    white-space: nowrap;
    color: ${({ theme }) => theme.colorPalette.white};
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    cursor: ${({ $cursor }) => $cursor};

    h3 {
      color: inherit;
      font-size: inherit;
      font-weight: inherit;
    }

    @media screen and (max-width: 800px) {
      line-height: 1.6rem;
    }
  }
`;
